import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import MultiCarousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import Container from 'react-bootstrap/Container';
import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Classes from '../whySunwest/index.module.css';

// ⬅️ Custom arrows moved outside the component to prevent re-renders
const CustomLeftArrow = ({ onClick }) => (
  <button
    className={Classes.customLeftArrow}
    onClick={onClick}
    aria-label="Previous Slide"
  >
    <img
      src="https://resources.swmc.com/swmc-images/leftArrowIcon.webp"
      alt="left"
    />
  </button>
);

const CustomRightArrow = ({ onClick }) => (
  <button
    className={Classes.customRightArrow}
    onClick={onClick}
    aria-label="Next Slide"
  >
    <img
      src="https://resources.swmc.com/swmc-images/rightArrowIcon.webp"
      alt="right"
    />
  </button>
);

const ReviewCarousel = () => {
  const [reviews, setReviews] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [infiniteStart, setInfiniteStart] = useState(1);
  const [isAutoPlay, setIsAutoPlay] = useState(true);

  const resumeTimeoutRef = useRef(null);
  const carouselRef = useRef(null);

  const fetchReviews = async (page) => {
    setIsLoading(true);
    
    try {
       const response = await axios.post(
          process.env.REACT_APP_NEW_REVIEWS_API,
          { pageNumber: page }
        );

      if (response.status === 200) {
        const newTestimonials = response.data;
     
        if (Array.isArray(newTestimonials)) {
          const transformedReviews = newTestimonials.map((ele) => {
            const date = new Date(ele.survey_completed_on);
            date.setDate(date.getDate() - 1);
            const monthNames = [
              "January", "February", "March", "April", "May", "June",
              "July", "August", "September", "October", "November", "December"
            ];
            const formattedDate = `on ${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
            const lastName = `${ele.customer_last_name.charAt(0)}.`;
            const firstName = ele.customer_first_name;

            return {
              id: ele.id,
              text: ele.review,
              rating: ele.rating,
              date: formattedDate,
              author: `${lastName}, ${formattedDate}`,
              name: `${firstName} ${lastName}`,
              showFullReview: false
            };
          });

          setReviews((prev) => [...prev, ...transformedReviews]);

          if (transformedReviews.length >= 8) {
            setPageNumber((prev) => prev + 1);
          }
        }
      } else {
        throw new Error('Request failed');
      }
    } catch (err) {
      // Handle error
    }
    setIsLoading(false);
  };

  const getNewTestimonials = (page) => {
    fetchReviews(page);
  };

  useEffect(() => {
    getNewTestimonials(infiniteStart);
  }, [infiniteStart]);

  const handleSlideChange = (currentSlide) => {
    const buffer = 4;
    if (currentSlide >= reviews.length - buffer && !isLoading && infiniteStart < 500) {
      const nextPage = pageNumber + 1;
      setPageNumber(nextPage);
      setInfiniteStart(infiniteStart + 1);
      getNewTestimonials(nextPage);
    }
  };

  const handleArrowClick = (direction) => {
    setIsAutoPlay(false);

    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
    }

    resumeTimeoutRef.current = setTimeout(() => {
      setIsAutoPlay(true);
    }, 20000);

    if (carouselRef.current) {
      if (direction === 'left') {
        carouselRef.current.previous();
      } else if (direction === 'right') {
        carouselRef.current.next();
      }
    }
  };

  const toggleFullReview = (reviewId) => {
    setReviews((prevReviews) =>
      prevReviews.map((review) => {
        if (review.id === reviewId) {
          return {
            ...review,
            showFullReview: !review.showFullReview
          };
        }
        return review;
      })
    );
  };

  const responsiveConfig = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 4,
      slidesToSlide: 1
    },
    tablet: {
      breakpoint: { max: 1024, min: 630 },
      items: 2,
      slidesToSlide: 1
    },
    mobile: {
      breakpoint: { max: 630, min: 0 },
      items: 1,
      slidesToSlide: 1
    }
  };

  return (
    <div>
      <img
        src={"https://stagingresources.swmc.com/swmc-images/Latest Testimonialsdesk.webp"}
        className={Classes.latestheading}
        loading='lazy'
        alt="Latest Testimonials"
      />
      <Container>
        <div className={Classes.carouselContainer}>
          <CustomLeftArrow onClick={() => handleArrowClick('left')} />
          <CustomRightArrow onClick={() => handleArrowClick('right')} />
          <MultiCarousel
            ref={carouselRef}
            responsive={responsiveConfig}
            autoPlay={isAutoPlay}
            autoPlaySpeed={1}
            infinite
            swipeable
            draggable
            arrows={true}
            customTransition="transform 3000ms linear"
            transitionDuration={3000}
            afterChange={handleSlideChange}
          >
            {reviews.map((review, index) => (
              <div key={review.id}>
                <Container>
                  <Card
                    className={`${Classes.card_Style} ${index % 2 === 0 ? Classes.orangeText : Classes.blueText}`}
                    id={Classes.review_item}
                  >
                    <Row>
                      <Col md={12}>
                        <div className={Classes.horizontal_author_details}>
                          <Row className={Classes.testimonials_rating}>
                            <Col className={Classes.testimonials_rating_col}>
                              <fieldset className={Classes.ratingcolor} disabled>
                                {[...Array(5)].map((_, i) => (
                                  <span
                                    key={i}
                                    className={`fa fa-star ${i < review.rating ? 'filled' : ''}`}
                                  ></span>
                                ))}
                              </fieldset>
                            </Col>
                            <Col>
                              <p className={Classes.datetext}>{review.date}</p>
                            </Col>
                          </Row>
                        </div>
                        <Row>
                          <p className={Classes.description}>
                            {review.showFullReview || review.text.length <= 150
                              ? review.text
                              : `${review.text.slice(0, 100)}...`}{' '}
                            {review.text.length > 100 && (
                              <span
                                className={Classes.seeMoreLink}
                                onClick={() => toggleFullReview(review.id)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') toggleFullReview(review.id);
                                }}
                              >
                                {review.showFullReview ? 'See Less' : 'See More'}
                              </span>
                            )}
                          </p>
                        </Row>
                        <Row>
                          <h6 className={Classes.name}>{review.name}</h6>
                        </Row>
                      </Col>
                    </Row>
                  </Card>
                </Container>
              </div>
            ))}
          </MultiCarousel>
          {isLoading && <p>Loading...</p>}
        </div>
      </Container>
    </div>
  );
};

export default ReviewCarousel;
